import { Link } from "react-router-dom";

/**
 * Tempo brand mark — canonical logo, rendered as inline SVG so it
 * scales crisply at every size and respects the design palette.
 *
 * Variants:
 *   • "mark" → rounded brand-slate tile with white "T." + rhythm bars
 *   • "full" → mark + "Tempo." wordmark in display type
 */
export default function Brand({
    variant = "mark",
    size = "md",
    to = "/",
    className = "",
    testId = "brand-link",
}) {
    const dims = {
        sm: { box: 32, type: "text-base", wordmarkH: 34 },
        md: { box: 40, type: "text-xl", wordmarkH: 60 },
        lg: { box: 56, type: "text-3xl", wordmarkH: 72 },
        xl: { box: 80, type: "text-5xl", wordmarkH: 156 },
    }[size] || { box: 40, type: "text-xl", wordmarkH: 60 };

    const wordmark = (
        <img
            src="/brand/tempo-logo.png"
            alt="Tempo"
            style={{
                height: dims.wordmarkH,
                width: "auto",
                filter: "contrast(1.08) saturate(1.05)",
            }}
            className="block select-none"
            draggable={false}
        />
    );

    const mark = (
        <svg
            width={dims.box}
            height={dims.box}
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0 select-none"
            aria-hidden="true"
        >
            <rect width="100" height="100" rx="22" fill="#4A6F8F" />
            {/* Rhythm bar — top left, above the T */}
            <rect x="22" y="32" width="14" height="6" rx="1.5" fill="#FFFFFF" />
            {/* T crossbar */}
            <rect x="36" y="28" width="42" height="10" rx="1.5" fill="#FFFFFF" />
            {/* T stem */}
            <rect x="52" y="28" width="10" height="46" rx="1.5" fill="#FFFFFF" />
            {/* Period dot */}
            <rect x="64" y="64" width="10" height="10" rx="1.5" fill="#FFFFFF" />
            {/* Rhythm bar — bottom right, below the T */}
            <rect x="56" y="76" width="14" height="6" rx="1.5" fill="#FFFFFF" />
        </svg>
    );

    const content =
        variant === "wordmark" ? (
            wordmark
        ) : variant === "full" ? (
            <span className="inline-flex items-center gap-4 leading-none">
                {mark}
                <img
                    src="/brand/tempo-logo.png"
                    alt="Tempo"
                    style={{ height: dims.wordmarkH * 1.4, width: "auto" }}
                    className="block select-none"
                    draggable={false}
                />
            </span>
        ) : (
            mark
        );

    if (!to) {
        return (
            <span
                className={`inline-flex items-center ${className}`}
                data-testid={testId}
            >
                {content}
            </span>
        );
    }

    return (
        <Link
            to={to}
            className={`inline-flex items-center group ${className}`}
            data-testid={testId}
        >
            {content}
        </Link>
    );
}
